import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PanAppState, PlatformService } from '@panamax/app-state';
import { PaymentsBasePage } from '@payments/payments-shared/payments-base/payments-base.page';
import { PaymentsService } from '@payments/services/payments.service';
import { Crumb } from '@shared/components/breadcrumbs/breadcrumbs.component';
import { CalendarComponent } from '@shared/components/calendar/calendar.component';
import { PageTitle } from '@shared/constants/page-titles.enum';
import { PATHS } from '@shared/constants/paths';
import { timezoneByCode } from '@shared/constants/timezone-constants';
import { ModalService } from '@shared/services/modal/modal.service';
import {
  CustomerBankAccount,
  CustomerPayment,
  CustomerReview,
  PaymentTransactionsViewModel,
} from '@usf/payment-types';
import { default as dayjs, default as utc } from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import timezone from 'dayjs/plugin/timezone';
import { Observable, Subject, Subscription } from 'rxjs';
import {
  debounceTime,
  distinctUntilKeyChanged,
  filter,
  take,
  takeUntil,
} from 'rxjs/operators';
import { ReviewPaymentService } from './services/review-payment.service';
import { ReviewPaymentAnalyticsService } from './tracking/analytics/review-payment-analytics.service';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localizedFormat);

export interface ReviewPaymentPageVM {
  autoPayCustomerNumbers: number[];
  breadcrumbs?: Crumb[];
  currentPayment?: CustomerPayment[];
  customers?: CustomerReview[];
  isBanksEditable: boolean;
  isSubmitEnabled: boolean;
  overallCustomerBankAccounts: CustomerBankAccount[];
  nonAutoPayCustomerCount: number;
  nonAutoPayTransactionCount: number;
  bankAccountError: any;
  submitError: any;
  transactions: PaymentTransactionsViewModel[];
  totalAmount: number;
  isLoading: boolean;
  isPaymentPreferencesOn?: boolean;
  isBankRoutingValidationOn?: boolean;
  showOrderNumber?: boolean;
}

@Component({
  selector: 'app-review-payment',
  templateUrl: './review-payment.page.html',
  styleUrls: ['./review-payment.page.scss'],
})
export class ReviewPaymentPage extends PaymentsBasePage {
  VALID_MAX_BUSINESS_DAYS = 10;
  vm$: Observable<ReviewPaymentPageVM>;
  isSubmitEnabled = false;
  selectedPaymentDate: dayjs.Dayjs;
  errorSubscription$ = new Subscription();
  validPaymentDates: dayjs.Dayjs[] = [];
  showSkeleton = true;
  destroyed$ = new Subject<void>();

  constructor(
    public router: Router,
    readonly route: ActivatedRoute,
    public modalController: ModalController,
    readonly paymentsService: PaymentsService,
    readonly platformService: PlatformService,
    private readonly reviewPaymentAnalyticsService: ReviewPaymentAnalyticsService,
    private readonly reviewPaymentService: ReviewPaymentService,
    private readonly panAppState: PanAppState,
    private modalService: ModalService,
    private translate: TranslateService,
  ) {
    super(router, route, paymentsService);
  }

  ionViewWillEnter() {
    super.ionViewWillEnter();
    this.reviewPaymentService
      .canActivate()
      .pipe(takeUntil(this.destroyed$))
      .subscribe();
    this.panAppState.setPageTitle(
      this.translate.instant(PageTitle.reviewAndSubmit),
    );
    this.vm$ = this.reviewPaymentService
      .getPayment$()
      .pipe(takeUntil(this.destroyed$));

    this.calculateValidPaymentDates();
    this.subscribeToVM();
  }

  ionViewDidEnter() {
    this.vm$
      .pipe(
        filter(vm =>
          vm.customers.some(
            customer => customer.autoPayTransactions.length > 0,
          ),
        ),
        take(1),
      )
      .subscribe(vm => {
        const alertMessage = this.translate.instant(
          'i18n.payments.reviewAndSubmit.autopayOneTimePaymentsAlert',
        );
        this.paymentsService.showToastMessage(alertMessage);
      });

    /* We debounce the values that come from the VM
     to avoid content flickering and jumping */
    this.vm$
      .pipe(debounceTime(800))
      .subscribe(vm => (this.showSkeleton = vm.isLoading));
  }

  subscribeToVM() {
    this.vm$
      .pipe(
        filter(vm => vm.customers.length > 0),
        take(1),
      )
      .subscribe(vm => {
        const customers = vm.customers.map(({ customerNumber }) => {
          return { customerNumber };
        });
        this.reviewPaymentService.fetchBankDetails(customers);
        this.reviewPaymentAnalyticsService.trackPageLoad(
          vm.transactions.length,
          vm.customers.length,
          vm.transactions.reduce(
            (acc, { paymentAmount }) => acc + (paymentAmount || 0),
            0,
          ),
        );
      });

    this.vm$
      .pipe(
        filter(vm => vm.overallCustomerBankAccounts.length > 0),
        take(1),
      )
      .subscribe(vm => {
        this.reviewPaymentService.setBankAccountsToPay(
          vm.customers
            .filter(customer => !!customer.selectedBankAccount)
            .map(customer => customer.selectedBankAccount),
        );
      });

    this.vm$
      .pipe(
        filter(vm => vm.isSubmitEnabled),
        take(1),
      )
      .subscribe(vm => {
        this.isSubmitEnabled = vm.isSubmitEnabled;
      });

    this.vm$
      .pipe(
        filter(vm => vm.currentPayment.length > 0),
        take(1),
      )
      .subscribe(() => {
        this.router.navigate([PATHS.PAYMENTS_PAYMENT_CONFIRMATION]);
      });

    this.errorSubscription$.add(
      this.vm$
        .pipe(
          distinctUntilKeyChanged('submitError'),
          filter(vm => !!vm.submitError),
        )
        .subscribe(() => {
          this.isSubmitEnabled = true;
          const message = this.translate.instant(
            'i18n.payments.reviewAndSubmit.failedSubmitErrors.fpgn-1001',
          );
          this.reviewPaymentService.showSubmitError(message);
        }),
    );
    this.errorSubscription$.add(
      this.vm$
        .pipe(
          distinctUntilKeyChanged('bankAccountError'),
          filter(vm => !!vm.bankAccountError),
        )
        .subscribe(() => {
          const message = this.translate.instant(
            'i18n.payments.reviewAndSubmit.failedBankAccountError',
          );
          this.reviewPaymentService.showBankAccountError(message);
        }),
    );
  }

  calculateValidPaymentDates() {
    let now = dayjs(new Date().toISOString()).tz(
      timezoneByCode.get('MT').location,
    ); //converts local time to MST Time
    this.selectedPaymentDate = this.pickNextBusinessDay(now);
    let lastValidDate = this.selectedPaymentDate.clone();
    for (let i = 0; i < this.VALID_MAX_BUSINESS_DAYS; i++) {
      this.validPaymentDates.push(lastValidDate);
      lastValidDate = this.pickNextBusinessDay(lastValidDate);
    }
  }

  ionViewWillLeave(): void {
    this.errorSubscription$.unsubscribe();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  pickNextBusinessDay(date: dayjs.Dayjs): dayjs.Dayjs {
    // check if we are before or after cutoff time (16:00hs MST, 11:00 MST on Sundays)
    let nextDay: dayjs.Dayjs;
    if (
      (date.day() === 0 && date.hour() < 11) ||
      (date.day() !== 0 && date.hour() < 16)
    ) {
      nextDay = date.add(1, 'day');
    } else {
      nextDay = date.add(2, 'day').startOf('day');
    }
    if (nextDay.day() === 0 || nextDay.day() === 6) {
      const daysToAdd = nextDay.day() === 0 ? 1 : 2;
      return nextDay.add(daysToAdd, 'day');
    }
    return nextDay;
  }

  dateFilter = (date: moment.Moment): boolean => {
    return this.validPaymentDates.some(
      validDate => validDate.format('YYYY-MM-DD') === date.format('YYYY-MM-DD'),
    );
  };

  async presentCalendarPopover(event: any) {
    event?.stopPropagation();
    this.modalService.setModalOptions(
      this.platformService.isTouch.value,
      CalendarComponent,
      {
        calendarTitle: this.translate.instant(
          'i18n.payments.reviewAndSubmit.calendarTitle',
        ),
        currentSelectedDate: new Date(this.selectedPaymentDate.format()),
        dateFilter: this.dateFilter,
        mindLocalTime: false,
      },
      'calendar-modal-desktop',
    );

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    modal.onDidDismiss().then((resp: any) => {
      if (resp?.data?.selectedDate) {
        this.selectedPaymentDate = dayjs(resp.data.selectedDate);
      }
    });

    return await modal.present();
  }

  submitPayment() {
    if (this.isSubmitEnabled) {
      this.vm$.pipe(take(1)).subscribe(vm => {
        const hasTransactions = vm.customers.some(
          customer => customer.transactions.length > 0,
        );
        if (hasTransactions) {
          let autoPayTransactions: PaymentTransactionsViewModel[] = [];
          for (const customer of vm.customers) {
            if (customer.autoPayTransactions.length) {
              autoPayTransactions = [
                ...autoPayTransactions,
                ...customer.autoPayTransactions,
              ];
            }
          }
          if (autoPayTransactions.length > 0) {
            this.paymentsService.selectInvoices(autoPayTransactions);
          }
          this.finallySubmitPayment();
        } else {
          this.paymentsService.showToastMessage(
            this.translate.instant(
              'i18n.payments.reviewAndSubmit.failedSubmitErrors.fpgn-1003',
            ),
          );
        }
      });
    }
  }

  private finallySubmitPayment() {
    // This does a take(1) to account for any auto pay transactions being deselected
    this.vm$.pipe(take(1)).subscribe(vm => {
      this.isSubmitEnabled = false;
      this.reviewPaymentService.submitPayment(
        +this.selectedPaymentDate.format('YYYYMMDD'),
      );
    });
  }
}
